import React from "react";
import classNames from "classnames";
import { Controller, useFormContext } from "react-hook-form";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import { useFormConfig } from "./FormContext";
import styles from "./FormField.module.scss";

/** Аргументы, передаваемые в render-callback {@link FormField}. Совпадают с параметрами render у Controller. */
export type FormFieldRenderArgs<TFieldValues extends FieldValues> = Parameters<
    NonNullable<ControllerProps<TFieldValues>["render"]>
>[0];

/**
 * Строит значение `data-testid` для поля формы (e2e).
 * @param formTestId префикс формы (из {@link Form} testId)
 * @param fieldName имя поля
 * @returns строка вида `form-field-name` или `formTestId-field-name`
 */
function getFieldTestId(formTestId: string | undefined, fieldName: string): string {
    const safeName = String(fieldName).replace(/\./g, "-");
    if (formTestId != null && formTestId !== "") {
        return `${formTestId}-field-${safeName}`;
    }
    return `form-field-${safeName}`;
}

/**
 * Пропсы компонента {@link FormField}.
 *
 * Рендер всегда идёт через `Controller`.
 * - **children = render-функция**: в callback приходят `{ field, fieldState }`.
 * - **children = ReactNode**: node рендерится как есть, но поле всё равно живёт в едином pipeline `Controller`.
 *
 * @template TFieldValues - тип значений формы (по умолчанию Record<string, unknown>)
 */
interface FormFieldProps<TFieldValues extends FieldValues = Record<string, unknown>>
    extends Omit<ControllerProps<TFieldValues>, "render" | "children"> {
    /**
     * Либо render-функция (получает `field`/`fieldState`), либо обычные дочерние элементы.
     */
    children: ((args: FormFieldRenderArgs<TFieldValues>) => React.ReactNode) | React.ReactNode;
    /**
     * Переопределение `data-testid` для обёртки поля (по умолчанию — из имени поля и {@link Form} testId).
     */
    testId?: string;
    /**
     * Показывать текст ошибки валидации под полем.
     * @default true
     */
    showErrorText?: boolean;
    /** Дополнительный CSS-класс корневого контейнера поля. */
    className?: string;
    /** Inline-стили корневого контейнера поля. */
    style?: React.CSSProperties;
}

/**
 * Обёртка поля формы.
 *
 * Рендерится через `Controller` для обоих вариантов children.
 * - С **render-функцией**: вызывает children с `{ field, fieldState }`.
 * - С **ReactNode**: рендерит node как есть (без отдельной ветки рендера).
 *
 * Текст ошибки отображается **в потоке** под полем и плавно появляется/скрывается (анимация
 * max-height), чтобы форма не «прыгала» резко при смене состояния валидации. Поведение и внешний
 * вид настраиваются через CSS-переменные на корне поля (см. `FormField.module.scss` — блок
 * `.formFieldRootVariables`): цвет, типографика, отступы, длительность и easing анимации, высота слота ошибки.
 *
 * @example
 * ```tsx
 * // Вариант 1: всё из form (типы из TFieldValues)
 * <Form initialValues={{ sign: "" }} onSubmit={...}>
 *   {(form) => (
 *     <Form.Field name="sign">
 *       <Input {...form.field("sign").getInputProps()} label="Код" />
 *     </Form.Field>
 *   )}
 * </Form>
 *
 * // Вариант 2: вручную из form
 * <Form.Field name="sign">
 *   <Input value={form.watch("sign")} onChange={(e) => form.setValue("sign", e.target.value)} error={!!form.formState.errors?.sign} />
 * </Form.Field>
 * ```
 */
function FormFieldInner<TFieldValues extends FieldValues = Record<string, unknown>>({
    name,
    control,
    children,
    testId: testIdOverride,
    showErrorText = true,
    className,
    style,
    ...controllerProps
}: FormFieldProps<TFieldValues>): React.ReactElement | null {
    const formContext = useFormContext<TFieldValues>();
    const config = useFormConfig();
    const ctrl = control ?? formContext.control;
    const resolvedTestId = testIdOverride ?? getFieldTestId(config?.testId, name as string);
    const renderChildren =
        typeof children === "function"
            ? (children as (args: FormFieldRenderArgs<TFieldValues>) => React.ReactNode)
            : () => children;

    return (
        <Controller
            name={name as FieldPath<TFieldValues>}
            control={ctrl}
            {...controllerProps}
            render={(controllerArgs) => {
                const errorMessage = controllerArgs.fieldState.error?.message;
                const hasError = typeof errorMessage === "string" && errorMessage.length > 0;
                return (
                    <div
                        className={classNames("form-field", styles.formFieldRootVariables, styles.formFieldRoot, className)}
                        style={style}
                        data-testid={resolvedTestId}
                        data-field-name={String(name)}
                        data-field-dirty={String(controllerArgs.fieldState.isDirty)}
                    >
                        {renderChildren(controllerArgs)}
                        {showErrorText && (
                            <div
                                className={classNames(styles.errorSlot, hasError && styles.errorSlotVisible)}
                                aria-hidden={!hasError}
                            >
                                <div className={styles.errorSlotInner}>
                                    {hasError && (
                                        <span className={styles.errorText} role="alert" title={errorMessage}>
                                            {errorMessage}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }}
        />
    );
}

export const FormField = FormFieldInner;
